<template>
  <div class="modal-card" style="width: auto">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <header class="modal-card-head">
      <p class="modal-card-title has-text-centered">
        {{
          currentCombo && currentCombo._id
            ? $tc("productCombo.editCombo")
            : $tc("productCombo.newCombo")
        }}
      </p>
    </header>

    <section class="modal-card-body">
      <div class="columns">
        <div class="column is-half is-offset-one-quarter">
          <b-field
            class="mb-5"
            :label="$t('productCombo.stores')"
            :type="storesFormType"
            :message="
              storesFormType === 'is-danger' ? $t('common.requiredField') : ''
            "
          >
            <div class="block" @focusout="storesHasBeenTouched = true">
              <b-checkbox
                v-for="s in comboEligibleStores"
                :key="s._id"
                v-model="selectedStoreIds"
                :native-value="s._id"
              >
                {{ s.storeInfo.name }}
              </b-checkbox>
            </div>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('productCombo.product')"
            label-position="on-border"
            :type="productFormType"
            :message="
              productFormType === 'is-danger' ? $t('common.requiredField') : ''
            "
          >
            <b-autocomplete
              v-model="productSearch"
              :data="filteredProductsArray"
              :placeholder="$t('productCombo.selectProduct')"
              field="n"
              clearable
              keep-first
              :disabled="!selectedStoreIds.length"
              @select="option => (selectedProduct = option)"
              @blur="productHasBeenTouched = true"
            >
              <template #empty>{{ $t("orders.noResultsFound") }}</template>
            </b-autocomplete>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('productCombo.buyQty')"
            label-position="on-border"
            :type="qtyFormType"
            :message="
              qtyFormType === 'is-danger'
                ? $t('productCombo.buyQty') + ' < ' + $t('productCombo.takeQty')
                : ''
            "
          >
            <b-input
              type="number"
              min="1"
              v-model="buyQty"
              @blur="qtyHasBeenTouched = true"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            ></b-input>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('productCombo.takeQty')"
            label-position="on-border"
            :type="qtyFormType"
          >
            <b-input
              type="number"
              min="1"
              v-model="takeQty"
              @blur="qtyHasBeenTouched = true"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            ></b-input>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('productCombo.startDate')"
            label-position="on-border"
            :type="startDateFormType"
            :message="
              startDateFormType === 'is-danger'
                ? $t('common.requiredField')
                : ''
            "
          >
            <b-datepicker
              v-model="startDate"
              icon="calendar-day"
              :locale="datepickerLocale"
              :mobile-native="false"
              append-to-body
              @blur="startDateHasBeenTouched = true"
            ></b-datepicker>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('productCombo.endDate')"
            label-position="on-border"
            :type="endDateFormType"
            :message="
              endDateFormType === 'is-danger'
                ? endDate
                  ? $t('errors.INVALID_COMBO_DATES')
                  : $t('common.requiredField')
                : ''
            "
          >
            <b-datepicker
              v-model="endDate"
              icon="calendar-day"
              :min-date="startDate"
              :locale="datepickerLocale"
              :mobile-native="false"
              append-to-body
              @blur="endDateHasBeenTouched = true"
            ></b-datepicker>
          </b-field>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot" style="justify-content: space-around;">
      <div class="columns">
        <div class="column">
          <button class="button" type="button" @click="$parent.close()">
            {{ $t("common.cancel") }}
          </button>

          <button
            class="button"
            :style="{
              backgroundColor: $primaryColor,
              color: $primaryColorContrast
            }"
            type="button"
            @click="createOrEdit"
            :disabled="!isAbleToRegister"
          >
            {{
              currentCombo && currentCombo._id
                ? $t("productCombo.editCombo")
                : $t("productCombo.createCombo")
            }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";

export default {
  mixins: [common],

  props: ["currentCombo"],

  data() {
    return {
      isLoading: false,
      storesHasBeenTouched: false,
      productHasBeenTouched: false,
      qtyHasBeenTouched: false,
      startDateHasBeenTouched: false,
      endDateHasBeenTouched: false,
      selectedStoreIds: [],
      productSearch: "",
      selectedProduct: null,
      buyQty: 1,
      takeQty: 2,
      startDate: null,
      endDate: null
    };
  },

  computed: {
    ...mapGetters(["stores", "products"]),

    // Formato de data do idioma da interface (pt_BR -> pt-BR) em vez do padrão do navegador
    datepickerLocale() {
      return this.$i18n.locale.replace("_", "-");
    },

    // Lojas integradas a sistemas externos (Hirota/idever) ainda não suportam combos
    comboEligibleStores() {
      return this.stores.filter(s => {
        const integration =
          s.configs && s.configs.integration && s.configs.integration.name;
        return (
          !integration || integration.trim() === "" || integration === "none"
        );
      });
    },

    filteredProductsArray() {
      return this.products.filter(
        option =>
          option.n
            .toString()
            .toLowerCase()
            .indexOf(this.productSearch.toLowerCase()) >= 0
      );
    },

    storesFormType() {
      return this.storesHasBeenTouched && this.selectedStoreIds.length === 0
        ? "is-danger"
        : "";
    },

    productFormType() {
      return this.productHasBeenTouched && !this.selectedProduct
        ? "is-danger"
        : "";
    },

    qtyFormType() {
      return this.qtyHasBeenTouched &&
        Number(this.buyQty) >= Number(this.takeQty)
        ? "is-danger"
        : "";
    },

    isDateOrderValid() {
      return (
        !this.startDate ||
        !this.endDate ||
        this.startOfDay(this.startDate) <= this.endOfDay(this.endDate)
      );
    },

    startDateFormType() {
      return this.startDateHasBeenTouched && !this.startDate ? "is-danger" : "";
    },

    endDateFormType() {
      return this.endDateHasBeenTouched &&
        (!this.endDate || !this.isDateOrderValid)
        ? "is-danger"
        : "";
    },

    isAbleToRegister() {
      return (
        this.selectedStoreIds.length > 0 &&
        this.selectedProduct &&
        this.selectedProduct._id &&
        Number(this.buyQty) >= 1 &&
        Number(this.takeQty) >= 1 &&
        Number(this.buyQty) < Number(this.takeQty) &&
        this.startDate &&
        this.endDate &&
        this.isDateOrderValid
      );
    }
  },

  methods: {
    createOrEdit() {
      return this.currentCombo && this.currentCombo._id
        ? this.updateCombo()
        : this.createCombo();
    },

    startOfDay(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },

    endOfDay(date) {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999);
      return d;
    },

    buildPayload() {
      return {
        stores: this.selectedStoreIds,
        productId: this.selectedProduct._id,
        buyQty: Number(this.buyQty),
        takeQty: Number(this.takeQty),
        // the picked end day is inclusive, so a same-day promo is still valid
        startDate: this.startOfDay(this.startDate),
        endDate: this.endOfDay(this.endDate)
      };
    },

    createCombo() {
      this.isLoading = true;
      this.$store
        .dispatch("createProductCombo", this.buildPayload())
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$emit("increment-key");
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("productCombo.comboHasBeenCreated"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.$parent.close();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateCombo() {
      this.isLoading = true;
      this.$store
        .dispatch("updateProductCombo", {
          comboId: this.currentCombo._id,
          ...this.buildPayload(),
          active: this.currentCombo.active
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$emit("increment-key");
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("productCombo.comboHasBeenUpdated"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.$parent.close();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  watch: {
    selectedStoreIds(newIds, oldIds) {
      const firstStoreId = newIds[0];
      if (firstStoreId && firstStoreId !== oldIds[0]) {
        this.$store.dispatch("getProducts", firstStoreId);
      }
    }
  },

  mounted() {
    if (this.currentCombo) {
      this.selectedStoreIds = this.currentCombo.stores.map(s => s._id);
      this.selectedProduct = this.currentCombo.product;
      this.productSearch = this.currentCombo.product
        ? this.currentCombo.product.n
        : "";
      this.buyQty = this.currentCombo.buyQty;
      this.takeQty = this.currentCombo.takeQty;
      this.startDate = this.currentCombo.startDate;
      this.endDate = this.currentCombo.endDate;

      if (this.selectedStoreIds[0]) {
        this.$store.dispatch("getProducts", this.selectedStoreIds[0]);
      }
    }
  }
};
</script>
